import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { BACKEND_URL } from '../utils/api';

const LivePreview = ({ files }) => {
  const [previewHtml, setPreviewHtml] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    generatePreview();
  }, [files]);

  // Only consider files in src/ or public/ for preview
  const pickAppFile = () => {
    const sourceFiles = Array.isArray(files) ? files : [];
    const frontendFiles = sourceFiles.filter(f => {
      const p = (f.path || '').replace(/\\/g, '/');
      // Handle both 'src/' and 'frontend/src/' paths
      return p.startsWith('src/') || p.startsWith('public/') ||
        p.startsWith('frontend/src/') || p.startsWith('frontend/public/') ||
        p.includes('/src/') || p.includes('/public/');
    });
    const appPathPattern = /app\.(js|jsx|ts|tsx)$/i;
    const appCandidates = frontendFiles.filter(file => appPathPattern.test(file.path || ''));
    const reactCandidates = appCandidates.filter(file => {
      const content = file.content || '';
      return /from\s+['\"]react['\"]/i.test(content) || /function\s+App\b/.test(content) || /const\s+App\s*=/.test(content);
    });
    if (reactCandidates.length > 0) {
      return reactCandidates[reactCandidates.length - 1];
    }
    if (appCandidates.length > 0) {
      return appCandidates[appCandidates.length - 1];
    }
    return frontendFiles.find(file => {
      const content = file.content || '';
      return /function\s+App\b/.test(content) || /const\s+App\s*=/.test(content);
    }) || null;
  };

  const generatePreview = () => {
    setLoading(true);
    try {
      const safeFiles = Array.isArray(files) ? files : [];
      const appFile = pickAppFile();
      const appCss = safeFiles.find(f => /App\.css$/i.test(f.path || ''));
      const indexCss = safeFiles.find(f => /index\.css$/i.test(f.path || ''));

      if (!appFile) {
        setError('No valid frontend (React) App component found. Only files in src/ or public/ are previewable.');
        setLoading(false);
        return;
      }

      const normalizePath = (path) => (path || '').replace(/\\/g, '/');

      const backendPattern = /(mongoose|express|cors|app\.use|Router\(|createRouter|createServer|req\s*,\s*res|res\.status|res\.json|res\.send|fastify|h3\.|defineEventHandler)/i;

      const componentFiles = safeFiles
        .filter(file => {
          const normalized = normalizePath(file?.path || '');
          // Handle both 'src/' and 'frontend/src/' paths
          const isSrcFile = normalized.startsWith('src/') ||
            normalized.startsWith('frontend/src/') ||
            normalized.includes('/src/');
          if (!isSrcFile) {
            return false;
          }

          if (/app\.(js|jsx|ts|tsx)$/i.test(normalized) || /\/index\.(js|jsx|ts|tsx)$/i.test(normalized)) {
            return false;
          }

          if (!/\.(js|jsx|ts|tsx)$/i.test(normalized)) {
            return false;
          }

          const content = file?.content || '';
          if (/\.test\.(js|jsx|ts|tsx)$/i.test(normalized)) {
            return false;
          }
          if (/jest\s*\.|@testing-library|describe\s*\(|it\s*\(|test\s*\(/i.test(content)) {
            return false;
          }
          if (/module\.exports|require\(/i.test(content)) {
            return false;
          }
          if (backendPattern.test(content)) {
            return false;
          }

          const hasComponentExport = /export\s+default\s+/i.test(content) || /export\s+(const|function|class)\s+/i.test(content);
          const hasJsx = /<\s*[A-Za-z]/.test(content) || /React\.createElement/i.test(content);
          const usesReact = /from\s+['"]react['"]/i.test(content) || /React\./.test(content);

          if (hasJsx || usesReact) {
            return true;
          }

          if (hasComponentExport) {
            const likelyFrontendPattern = /(useState|useEffect|useMemo|useCallback|useReducer|useRef|useContext|useNavigate|useLocation|useParams|useForm|styled\.|className\s*=|\bCSS\b)/i;
            return likelyFrontendPattern.test(content);
          }

          return false;
        })
        .sort((a, b) => normalizePath(a.path).localeCompare(normalizePath(b.path)));

      const sanitizeForPreview = (code) => {
        if (!code) return '';
        let transformed = code;
        transformed = transformed.replace(/import[^;]*;?/g, '');
        transformed = transformed.replace(/export\s+default\s+function\s+([A-Za-z0-9_]+)/g, 'function $1');
        transformed = transformed.replace(/export\s+default\s+class\s+([A-Za-z0-9_]+)/g, 'class $1');
        transformed = transformed.replace(/export\s+default\s+/g, '');
        transformed = transformed.replace(/export\s+const\s+/g, 'const ');
        transformed = transformed.replace(/export\s+let\s+/g, 'let ');
        transformed = transformed.replace(/export\s+var\s+/g, 'var ');
        transformed = transformed.replace(/export\s+function\s+/g, 'function ');
        transformed = transformed.replace(/export\s+class\s+/g, 'class ');
        transformed = transformed.replace(/export\s+\{[^}]*\};?/g, '');
        return transformed;
      };

      const componentDefinitions = componentFiles
        .map(file => `// ${normalizePath(file.path)}\n${sanitizeForPreview(file.content)}`)
        .join('\n\n');

      const previewApiBase = BACKEND_URL.replace(/\/+$/, '');

      // Try to discover a reasonable fallback component to render if App is missing
      let fallbackComponentName = null;
      for (const file of componentFiles) {
        const content = file.content || '';
        const m1 = content.match(/export\s+default\s+function\s+([A-Za-z0-9_]+)/);
        const m2 = content.match(/export\s+default\s+class\s+([A-Za-z0-9_]+)/);
        const m3 = content.match(/export\s+default\s+([A-Za-z0-9_]+)\s*;?/);
        if (m1 && m1[1]) { fallbackComponentName = m1[1]; break; }
        if (m2 && m2[1]) { fallbackComponentName = m2[1]; break; }
        if (m3 && m3[1]) { fallbackComponentName = m3[1]; break; }
      }

      const rawAppJs = appFile.content || '';
      const fallbackSnippets = new Map();
      const ensureFallback = (key, snippet) => {
        if (!fallbackSnippets.has(key)) {
          fallbackSnippets.set(key, snippet);
        }
      };

      const importStatements = rawAppJs.match(/import[^;]*;?/g) || [];
      importStatements.forEach(statement => {
        const moduleMatch = statement.match(/from\s+['"]([^'"]+)['"]/);
        const moduleName = moduleMatch ? moduleMatch[1] : null;

        if (moduleName === 'react-redux') {
          ensureFallback('react-redux', `
var __previewStore = {
  getState: () => ({}),
  subscribe: () => () => {},
  dispatch: () => {}
};
var Provider = typeof Provider !== 'undefined' ? Provider : function Provider(props) {
  return React.createElement(React.Fragment, null, props.children);
};
var useDispatch = typeof useDispatch !== 'undefined' ? useDispatch : function useDispatch() {
  return function dispatch() {};
};
var useSelector = typeof useSelector !== 'undefined' ? useSelector : function useSelector(selector) {
  try {
    return selector ? selector(__previewStore.getState()) : __previewStore.getState();
  } catch (err) {
    return {};
  }
};
`);
        }

        if (moduleName === 'react-router-dom') {
          ensureFallback('react-router-dom', `
var BrowserRouter = typeof BrowserRouter !== 'undefined' ? BrowserRouter : function BrowserRouter(props) {
  return React.createElement(React.Fragment, null, props.children);
};
var Router = typeof Router !== 'undefined' ? Router : BrowserRouter;
var Route = typeof Route !== 'undefined' ? Route : function Route(props) {
  const Component = props.component || props.element || (() => props.children || null);
  return React.createElement(React.Fragment, null, React.createElement(Component));
};
var Switch = typeof Switch !== 'undefined' ? Switch : function Switch(props) {
  return React.createElement(React.Fragment, null, props.children);
};
var Routes = typeof Routes !== 'undefined' ? Routes : Switch;
var Link = typeof Link !== 'undefined' ? Link : function Link(props) {
  const { to, children, ...rest } = props;
  return React.createElement('a', { href: to || '#', ...rest }, children);
};
var useHistory = typeof useHistory !== 'undefined' ? useHistory : function useHistory() {
  return { push: () => {} };
};
var useNavigate = typeof useNavigate !== 'undefined' ? useNavigate : function useNavigate() {
  return () => {};
};
`);
        }

        if (moduleName === 'styled-components') {
          ensureFallback('styled-components', `
var styled = typeof styled !== 'undefined' ? styled : new Proxy(function () {}, {
  get: function (_, tag) {
    return function () {
      return function StyledComponent(props) {
        return React.createElement(tag, props, props && props.children);
      };
    };
  },
  apply: function () {
    return function StyledComponent(props) {
      return React.createElement('div', props, props && props.children);
    };
  }
});
`);
        }

        if (moduleName === 'socket.io-client') {
          ensureFallback('socket.io-client', `
var io = typeof io !== 'undefined' ? io : function () {
  return {
    on: function () {},
    emit: function () {},
    disconnect: function () {}
  };
};
`);
        }

        if (moduleName === 'axios') {
          ensureFallback('axios', `
var __previewMockProducts = [
  {
    _id: 'demo-1',
    name: 'QuantumFlux Motherboard',
    price: 449.99,
    description: 'Enterprise-grade ATX motherboard engineered for extreme throughput workloads.',
    stock: 14,
    imageUrl: 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=800&q=80'
  },
  {
    _id: 'demo-2',
    name: 'CarbonEdge Server Chassis',
    price: 1199.5,
    description: 'Lightweight carbon fiber 4U chassis with hot-swap NVMe bays and modular cooling.',
    stock: 6,
    imageUrl: 'https://images.unsplash.com/photo-1587202372775-98927d1e1c0f?auto=format&fit=crop&w=800&q=80'
  },
  {
    _id: 'demo-3',
    name: 'Helios GPU Array',
    price: 3499.0,
    description: 'Four-card liquid-cooled GPU array tuned for ML inference and advanced rendering.',
    stock: 3,
    imageUrl: 'https://images.unsplash.com/photo-1618005198919-d3d4b5a92eee?auto=format&fit=crop&w=800&q=80'
  }
];
var axios = typeof axios !== 'undefined' ? axios : {
  get: async function (url) {
    if (url && url.includes('/api/products')) {
      return { data: __previewMockProducts };
    }
    return { data: [] };
  },
  post: async function () { return { data: {} }; },
  put: async function () { return { data: {} }; },
  delete: async function () { return { data: {} }; }
};
`);
        }
      });

      const fallbackCode = Array.from(fallbackSnippets.values()).join('\n');
      let processedAppJs = rawAppJs;
      const appExtension = (appFile.path || '').split('.').pop()?.toLowerCase();
      const isTypeScript = appExtension === 'ts' || appExtension === 'tsx';

      let defaultExportName = null;
      let defaultExportInlined = false;

      const defaultFunctionMatch = rawAppJs.match(/export\s+default\s+function\s+([A-Za-z0-9_]+)/);
      const defaultClassMatch = rawAppJs.match(/export\s+default\s+class\s+([A-Za-z0-9_]+)/);
      const defaultNamedMatch = rawAppJs.match(/export\s+default\s+([A-Za-z0-9_]+)\s*;?/);

      if (defaultFunctionMatch) {
        defaultExportName = defaultFunctionMatch[1];
        processedAppJs = processedAppJs.replace(/export\s+default\s+function\s+([A-Za-z0-9_]+)/, (_, name) => `function ${name}`);
      } else if (defaultClassMatch) {
        defaultExportName = defaultClassMatch[1];
        processedAppJs = processedAppJs.replace(/export\s+default\s+class\s+([A-Za-z0-9_]+)/, (_, name) => `class ${name}`);
      } else if (rawAppJs.includes('export default')) {
        if (defaultNamedMatch) {
          defaultExportName = defaultNamedMatch[1];
          processedAppJs = processedAppJs.replace(/export\s+default\s+([A-Za-z0-9_]+)\s*;?/, '');
        } else {
          processedAppJs = processedAppJs.replace(/export\s+default\s+/, 'const App = ');
          defaultExportInlined = true;
        }
      }

      processedAppJs = processedAppJs.replace(/import\s+.*?from\s+['"].*?['"];?\s*/g, '');
      processedAppJs = processedAppJs.replace(/import\s+['"].*?['"];?\s*/g, '');
      processedAppJs = processedAppJs.replace(/export\s+type\s+\{[^}]*\};?\s*/g, '');
      processedAppJs = processedAppJs.replace(/export\s+(const|function|class)\s+/g, '$1 ');
      processedAppJs = processedAppJs.replace(/export\s+default\s+/g, '');
      processedAppJs = processedAppJs.replace(/export\s+/g, '');

      let hasAppDefinition = /function\s+App\b/.test(processedAppJs) || /const\s+App\s*=/.test(processedAppJs) || /class\s+App\b/.test(processedAppJs);

      if (!hasAppDefinition) {
        if (defaultExportInlined) {
          hasAppDefinition = true;
        } else if (defaultExportName && defaultExportName !== 'App') {
          processedAppJs = `${processedAppJs}\nconst App = ${defaultExportName};`;
          hasAppDefinition = true;
        } else if (fallbackComponentName) {
          processedAppJs = `${processedAppJs}\nconst App = ${fallbackComponentName};`;
          hasAppDefinition = true;
        }
      }

      if (!hasAppDefinition) {
        processedAppJs = `${processedAppJs}\nconst App = () => null;`;
      }

      let previewScript = '';
      if (componentDefinitions.trim().length > 0) {
        const normalizedComponents = componentDefinitions.replace(/fetch\(\s*['"]\/api/gi, `fetch('${previewApiBase}/api`);
        previewScript += `${normalizedComponents}\n\n`;
      }
      if (fallbackCode) {
        previewScript += `${fallbackCode}\n`;
      }
      previewScript += processedAppJs;

      const presetsAttr = isTypeScript ? ' data-presets="typescript,react"' : ' data-presets="react"';
      const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Live Preview</title>
  <script crossorigin src="https://unpkg.com/react@18/umd/react.production.min.js"></script>
  <script crossorigin src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"></script>
  <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
  <style>
    * {
      box-sizing: border-box;
    }
    body {
      margin: 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
        'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
        sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    #root {
      min-height: 100vh;
    }
    ${indexCss ? indexCss.content : ''}
    ${appCss ? appCss.content : ''}
  </style>
  <script>
    window.addEventListener('error', (e) => {
      document.body.innerHTML =
        '<div style="padding: 20px; color: #e74c3c; font-family: monospace;">' +
        '<h3>Error in Preview:</h3>' +
        '<pre>' + e.message + '</pre>' +
        '<pre>' + (e.error && e.error.stack ? e.error.stack : '') + '</pre>' +
        '</div>';
    });

    console.log = function(...args) {
      window.parent.postMessage({ type: 'console.log', args }, '*');
    };
    
    console.error = function(...args) {
      window.parent.postMessage({ type: 'console.error', args }, '*');
    };
  </script>
</head>
<body>
  <div id="root"></div>
  <script type="text/babel"${presetsAttr}>
    const { useState, useEffect, useRef } = React;

    ${previewScript}

    const root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(<App />);
  </script>
</body>
</html>
      `;
      setPreviewHtml(html);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    generatePreview();
    if (iframeRef.current) {
      iframeRef.current.src = iframeRef.current.src;
    }
  };

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'console.error') {
        setError(event.data.args.join(' '));
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <div className="h-full flex flex-col bg-slate-950">
      {/* Premium Preview Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 px-4 py-2.5 border-b border-slate-700/50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="h-2.5 w-2.5 rounded-full bg-green-500 shadow-lg shadow-green-500/50"></div>
            <div className="h-2.5 w-2.5 rounded-full bg-yellow-500 opacity-60"></div>
            <div className="h-2.5 w-2.5 rounded-full bg-red-500 opacity-60"></div>
          </div>
          <span className="text-sm font-medium text-slate-300">Live Preview</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          disabled={loading}
          className="text-slate-400 hover:text-white hover:bg-slate-700/50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {error ? (
        <div className="flex-1 flex items-center justify-center bg-gradient-to-b from-red-950/30 to-slate-950 p-6">
          <div className="text-center max-w-md">
            <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 inline-block mb-4">
              <AlertCircle className="h-10 w-10 text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-red-300 mb-2">Preview Error</h3>
            <pre className="text-sm text-red-300/80 bg-red-950/50 border border-red-500/20 p-4 rounded-lg overflow-auto text-left max-h-48">
              {error}
            </pre>
            <Button onClick={handleRefresh} className="mt-4 bg-slate-800 hover:bg-slate-700 text-white border border-slate-600" variant="outline">
              Try Again
            </Button>
          </div>
        </div>
      ) : (
        <iframe
          ref={iframeRef}
          srcDoc={previewHtml}
          className="flex-1 w-full border-0 bg-white"
          sandbox="allow-scripts allow-same-origin"
          title="Live Preview"
        />
      )}
    </div>
  );
};

export default LivePreview;
